import { type CollaboratorDetails } from "@vega-api/generated/types/collaboratorDetails";
import { Avatar } from "@vega-core/components/Avatar/Avatar";
import { Checkbox } from "@vega-core/components/shadcn/checkbox";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { formatFullName } from "@vega-core/utils/formatString";

type CollaboratorSelectionRowProps = {
  collaborator: CollaboratorDetails;
  roleCount: number;
  isSelected: boolean;
  onToggle: () => void;
};

const formatRoleCount = (count: number): string => {
  if (count === 0) return m.settings_collaborators_manageRoles_noAssignments();

  if (count === 1) return m.settings_collaborators_manageRoles_oneRole();

  return m.settings_collaborators_manageRoles_rolesCount({
    count: String(count),
  });
};

export const CollaboratorSelectionRow = ({
  collaborator,
  roleCount,
  isSelected,
  onToggle,
}: CollaboratorSelectionRowProps) => {
  const isDisabled = roleCount === 0;
  const fullName = formatFullName({
    firstName: collaborator.firstName,
    middleName: collaborator.middleName,
    lastName: collaborator.lastName,
  });
  const initial = collaborator.firstName
    ? collaborator.firstName[0]
    : collaborator.email[0];
  const checkboxId = `manage-roles-row-${collaborator.id}`;

  return (
    <label
      aria-disabled={isDisabled}
      className={cnTwMerge(
        "flex items-center gap-3 px-4 py-3 rounded-small",
        isDisabled
          ? "pointer-events-none opacity-50"
          : "cursor-pointer hover:bg-background/subtle",
      )}
      data-testid={`manage-roles-row-${collaborator.id}`}
      htmlFor={checkboxId}
    >
      <Checkbox
        checked={isSelected}
        data-testid={`manage-roles-row-checkbox-${collaborator.id}`}
        disabled={isDisabled}
        id={checkboxId}
        onCheckedChange={onToggle}
      />
      <Avatar initials={initial} />
      <div className="flex flex-col items-start flex-1 min-w-0 text-left">
        <span className="capitalize body-base-medium text-text/primary truncate w-full">
          {fullName}
        </span>
        <span className="lowercase body-small-regular text-text/secondary truncate w-full">
          {collaborator.email}
        </span>
      </div>
      <span
        className={cnTwMerge(
          "body-small-regular shrink-0",
          isDisabled ? "text-text/tertiary" : "text-text/secondary",
        )}
        data-testid={`manage-roles-role-count-${collaborator.id}`}
      >
        {formatRoleCount(roleCount)}
      </span>
    </label>
  );
};
